import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGameStore from '../../stores/gameStore';
import {
  getPreCountdownRemainingSeconds,
  isTimerInPreCountdown,
  parseTimerState,
  type TimerState,
} from '../../utils/timerState';

export type TimerDisplayType = 'blind' | 'stopwatch' | 'countdown' | 'min' | 'deadline-countdown';

export interface TimerDisplayProps {
  timerVar: string;
  displayType?: TimerDisplayType | undefined;
  className?: string | undefined;
  /** Start time (UTC ms) from engine; when set, component ticks locally without reading the variable again. */
  startedAt?: string | undefined;
  /** Stop time (UTC ms) when timer was stopped. */
  stoppedAt?: string | undefined;
  /** Max seconds for countdown mode (from engine). */
  maxSeconds?: string | undefined;
  /** UTC epoch ms deadline for deadline-countdown mode. */
  targetAt?: string | undefined;
  armedAt?: string | undefined;
  preCountdownSeconds?: string | undefined;
  preCountdownAudioId?: string | undefined;
}

function parseOptionalMs(value: string | undefined): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseOptionalSeconds(value: string | undefined): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function getElapsedSeconds(state: TimerState, nowMs: number): number {
  if (state.startedAt === null) return 0;
  const endMs = state.stoppedAt !== null ? state.stoppedAt : nowMs;
  return Math.floor(Math.max(0, endMs - state.startedAt) / 1000);
}

function getRemainingSeconds(state: TimerState, nowMs: number): number | null {
  if (state.mode !== 'max' || state.maxSeconds === null) return null;
  const elapsed = getElapsedSeconds(state, nowMs);
  return Math.max(0, state.maxSeconds - elapsed);
}

function getDeadlineRemainingSeconds(state: TimerState, nowMs: number): number | null {
  if (state.mode !== 'deadline' || state.targetAt === null || state.stoppedAt !== null) return null;
  return Math.max(0, Math.floor((state.targetAt - nowMs) / 1000));
}

function formatMMSS(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(m)}:${pad(s)}`;
}

/** Hours, minutes, and seconds remaining, e.g. 05:23:41. */
function formatHMS(totalSeconds: number): string {
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
}

function renderPreCountdown(remaining: number, className: string) {
  return (
    <div
      className={`timer-display timer-display-pre-countdown timer-display-led ${className}`}
      data-testid="timer-display-pre-countdown"
      role="timer"
      aria-live="polite"
      aria-label={`Get ready: ${remaining}`}
    >
      <span className="timer-display-pre-countdown-label">Get ready</span>
      <span className="timer-display-value timer-display-pre-countdown-value">{remaining}</span>
    </div>
  );
}

/**
 * Renders a timer. When startedAt (and optionally stoppedAt/maxSeconds/targetAt) are passed from the engine
 * markup, the component updates itself locally without reading the variable again.
 */
const TimerDisplay: React.FC<TimerDisplayProps> = ({
  timerVar,
  displayType = 'stopwatch',
  className = '',
  startedAt: startedAtProp,
  stoppedAt: stoppedAtProp,
  maxSeconds: maxSecondsProp,
  targetAt: targetAtProp,
  armedAt: armedAtProp,
  preCountdownSeconds: preCountdownSecondsProp,
  preCountdownAudioId: preCountdownAudioIdProp,
}) => {
  const rawValue = useGameStore(
    useCallback((state) => state.userVariableValues[timerVar]?.value ?? '', [timerVar])
  );

  const stateFromStore = useMemo(() => parseTimerState(rawValue), [rawValue]);
  const startedAtMs = parseOptionalMs(startedAtProp);
  const stoppedAtMs = parseOptionalMs(stoppedAtProp);
  const maxSecondsFromProp = parseOptionalSeconds(maxSecondsProp);
  const targetAtMs = parseOptionalMs(targetAtProp);
  const armedAtMs = parseOptionalMs(armedAtProp);
  const preCountdownSecondsFromProp = parseOptionalSeconds(preCountdownSecondsProp);

  const useProps = startedAtMs !== null || targetAtMs !== null || armedAtMs !== null;
  const state = useMemo(() => {
    const base = useProps
      ? {
          ...stateFromStore,
          startedAt: startedAtMs ?? stateFromStore.startedAt,
          stoppedAt: stoppedAtMs,
          maxSeconds: maxSecondsFromProp ?? stateFromStore.maxSeconds,
          targetAt: targetAtMs ?? stateFromStore.targetAt,
          armedAt: armedAtMs ?? stateFromStore.armedAt,
          preCountdownSeconds:
            preCountdownSecondsFromProp ?? stateFromStore.preCountdownSeconds,
          preCountdownAudioId:
            preCountdownAudioIdProp ?? stateFromStore.preCountdownAudioId,
        }
      : stateFromStore;

    const maxSec = base.maxSeconds;
    const targetAt = base.targetAt;
    const mode =
      displayType === 'deadline-countdown' || targetAt !== null
        ? 'deadline'
        : maxSec !== null
          ? 'max'
          : base.mode;

    return { ...base, mode } as TimerState;
  }, [
    useProps,
    startedAtMs,
    stoppedAtMs,
    maxSecondsFromProp,
    targetAtMs,
    armedAtMs,
    preCountdownSecondsFromProp,
    preCountdownAudioIdProp,
    stateFromStore,
    displayType,
  ]);

  const isRunning = state.startedAt !== null && state.stoppedAt === null;
  const isDeadlineActive =
    displayType === 'deadline-countdown' &&
    state.targetAt !== null &&
    state.stoppedAt === null;

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isRunning && !isDeadlineActive) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isRunning, isDeadlineActive]);

  const inPreCountdown = useMemo(
    () => isTimerInPreCountdown(state, now),
    [state, now]
  );
  const preCountdownRemaining = useMemo(
    () => getPreCountdownRemainingSeconds(state, now),
    [state, now]
  );
  const elapsed = useMemo(() => getElapsedSeconds(state, now), [state, now]);
  const remaining = useMemo(() => getRemainingSeconds(state, now), [state, now]);
  const deadlineRemaining = useMemo(() => getDeadlineRemainingSeconds(state, now), [state, now]);

  if (inPreCountdown) {
    return renderPreCountdown(preCountdownRemaining, className);
  }

  if (displayType === 'blind') {
    return (
      <div
        className={`timer-display timer-display-blind ${className}`}
        data-testid="timer-display-blind"
        role="timer"
        aria-label="Timing in progress"
      >
        <div className="timer-display-blind-clock" aria-hidden>
          <div className="timer-display-blind-clock-minute-hand" />
          <div className="timer-display-blind-clock-hand" />
          <div className="timer-display-blind-clock-center" />
        </div>
      </div>
    );
  }

  if (displayType === 'deadline-countdown') {
    const rem = deadlineRemaining;
    const isZero = rem !== null && rem <= 0;
    return (
      <div
        className={`timer-display timer-display-countdown timer-display-led ${className}`}
        data-testid="timer-display-deadline-countdown"
        role="timer"
        aria-live="polite"
        aria-label={isZero ? "Time's up" : `Time remaining: ${rem !== null ? formatHMS(rem) : '0:00:00'}`}
      >
        {isZero ? (
          <span className="timer-display-times-up">Time's up</span>
        ) : (
          <span className="timer-display-value">{rem !== null ? formatHMS(rem) : '0:00:00'}</span>
        )}
      </div>
    );
  }

  if (displayType === 'countdown') {
    const rem = remaining;
    const isZero = rem !== null && rem <= 0;
    return (
      <div
        className={`timer-display timer-display-countdown timer-display-led ${className}`}
        data-testid="timer-display-countdown"
        role="timer"
        aria-live="polite"
        aria-label={isZero ? "Time's up" : `Time remaining: ${rem !== null ? formatMMSS(rem) : '0:00'}`}
      >
        {isZero ? (
          <span className="timer-display-times-up">Time's up</span>
        ) : (
          <span className="timer-display-value">{rem !== null ? formatMMSS(rem) : '0:00'}</span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`timer-display timer-display-elapsed timer-display-led ${className}`}
      data-testid="timer-display-elapsed"
      role="timer"
      aria-live="polite"
      aria-label={`Elapsed time: ${formatMMSS(elapsed)}`}
    >
      <span className="timer-display-value">{formatMMSS(elapsed)}</span>
    </div>
  );
};

export default TimerDisplay;
